import { supabase } from '../lib/supabase';

class ArresteeService {
  // Search arrestee records
  async searchRecords(searchParams) {
    try {
      const { firstName, lastName, dateOfBirth, caseNumber, bookingNumber } = searchParams;
      
      const { data, error } = await supabase?.rpc('search_arrestee_records', {
        p_first_name: firstName || null,
        p_last_name: lastName || null,
        p_date_of_birth: dateOfBirth || null,
        p_case_number: caseNumber || null,
        p_booking_number: bookingNumber || null
      });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Get all records with pagination
  async getRecords(page = 1, limit = 50) {
    try {
      const offset = (page - 1) * limit;
      
      const { data, error, count } = await supabase?.from('arrestee_records')?.select(`
          *,
          upload_batch:upload_batches(
            filename,
            upload_date,
            uploaded_by
          )
        `, { count: 'exact' })?.eq('status', 'active')?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      return { 
        success: true, 
        data: data || [], 
        total: count || 0,
        page,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      return { 
        success: false, 
        error: error?.message, 
        data: [], 
        total: 0, 
        page: 1, 
        totalPages: 0 
      };
    }
  }

  // Get single record by ID
  async getRecord(recordId) {
    try {
      const { data, error } = await supabase?.from('arrestee_records')?.select(`
          *,
          upload_batch:upload_batches(
            filename,
            upload_date,
            uploaded_by,
            user_profile:user_profiles(
              full_name
            )
          )
        `)?.eq('id', recordId)?.single();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message, data: null };
    }
  }

  // Update record
  async updateRecord(recordId, updates) {
    try {
      const { data, error } = await supabase?.from('arrestee_records')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', recordId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message, data: null };
    }
  }

  // Create new record
  async createRecord(recordData) {
    try {
      const { data, error } = await supabase?.from('arrestee_records')?.insert(recordData)?.select()?.single();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message, data: null };
    }
  }

  // Delete record (mark as archived)
  async deleteRecord(recordId) {
    try {
      const { data, error } = await supabase?.from('arrestee_records')?.update({ 
          status: 'archived',
          updated_at: new Date()?.toISOString()
        })?.eq('id', recordId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message, data: null };
    }
  }

  // Check for duplicates
  async checkDuplicate(firstName, lastName, dateOfBirth, excludeId = null) {
    try {
      const { data, error } = await supabase?.rpc('find_duplicate_record', {
        p_first_name: firstName,
        p_last_name: lastName,
        p_date_of_birth: dateOfBirth,
        p_current_id: excludeId
      });

      if (error) {
        throw error;
      }

      return { success: true, duplicateId: data };
    } catch (error) {
      return { success: false, error: error?.message, duplicateId: null };
    }
  }

  // Log search activity
  async logSearch(searchTerms, resultsCount) {
    try {
      const { data, error } = await supabase?.from('search_history')?.insert({
          search_terms: searchTerms,
          results_count: resultsCount
        })?.select()?.single();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  // Get records for a specific person (by name and DOB)
  async getPersonRecords(firstName, lastName, dateOfBirth) {
    try {
      const { data, error } = await supabase?.from('arrestee_records')?.select(`
          *,
          upload_batch:upload_batches(
            filename,
            upload_date
          )
        `)?.ilike('first_name', firstName)?.ilike('last_name', lastName)?.eq('date_of_birth', dateOfBirth)?.eq('status', 'active')?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Add flexible search method to match frontend usage
  async flexibleSearch(searchParams) {
    try {
      const { 
        firstName, 
        lastName, 
        dateOfBirth, 
        facility, 
        agency, 
        status, 
        arrestDateFrom, 
        arrestDateTo,
        releaseDateFrom,
        releaseDateTo,
        chargeQuery,
        warrantQuery,
        city,
        zip,
        bookingNumber,
        caseNumber,
        fuzzy = true,
        page = 1, 
        pageSize = 25 
      } = searchParams;
      
      // Use the enhanced search function with better fuzzy matching
      const { data, error } = await supabase?.rpc('search_persons_enhanced', {
        p_first_name: firstName || null,
        p_last_name: lastName || null,
        p_fuzzy: fuzzy,
        p_page: page,
        p_page_size: pageSize
      });

      if (error) {
        // If enhanced search fails, fall back to flexible search
        console.warn('Enhanced search failed, trying flexible search:', error);
        
        const fallbackResult = await supabase?.rpc('search_flex', {
          p_first: firstName || null,
          p_last: lastName || null,
          p_dob: dateOfBirth || null,
          p_facility: facility || null,
          p_agency: agency || null,
          p_status: status || null,
          p_arrest_date_from: arrestDateFrom || null,
          p_arrest_date_to: arrestDateTo || null,
          p_release_date_from: releaseDateFrom || null,
          p_release_date_to: releaseDateTo || null,
          p_charge_q: chargeQuery || null,
          p_warrant_q: warrantQuery || null,
          p_city: city || null,
          p_zip: zip || null,
          p_booking_no: bookingNumber || null,
          p_case_no: caseNumber || null,
          p_fuzzy: fuzzy,
          p_page: page,
          p_page_size: pageSize
        });

        if (fallbackResult?.error) {
          throw fallbackResult?.error;
        }

        return { 
          success: true, 
          data: fallbackResult?.data || [],
          pagination: {
            page,
            pageSize,
            total: fallbackResult?.data?.length || 0,
            totalPages: Math.ceil((fallbackResult?.data?.length || 0) / pageSize)
          }
        };
      }

      return { 
        success: true, 
        data: data || [],
        pagination: {
          page,
          pageSize,
          total: data?.length || 0,
          totalPages: Math.ceil((data?.length || 0) / pageSize)
        }
      };
    } catch (error) {
      console.error('Flexible search error:', error);
      return { 
        success: false, 
        error: error?.message || 'Search failed', 
        data: [],
        pagination: {
          page: 1,
          pageSize: 25,
          total: 0,
          totalPages: 0
        }
      };
    }
  }

  // Add search diagnostics method for debugging
  async diagnoseSearch(firstName = null, lastName = null) {
    try {
      const { data, error } = await supabase?.rpc('diagnose_search_issue', {
        search_first_name: firstName,
        search_last_name: lastName
      });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Search diagnostics error:', error);
      return { success: false, error: error?.message, data: [] };
    }
  }
}

export default new ArresteeService();